import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { invalidateClaimsQueries } from '@/admin/hooks/use-claims'

/** Composite address shape the backend ADDRESS host attribute expects. */
export interface HoaCommunityAddress {
  addressLine1: string
  addressLine2: string
  locality: string
  region: string
  postalCode: string
  countryCode: string
}

export interface HoaCommunityCreatePayload {
  hoa_name: string
  total_units?: number | null
  manager_email: string
  manager_first_name?: string
  manager_last_name?: string
  manager_phone?: string
  /** Optional: link this community to an HOA / Developer Network partner. */
  developer_partnership_uuid?: string | null
  address: HoaCommunityAddress
}

export interface HoaCommunityResponse {
  uuid: string
  name: string
  hoa_name: string
  total_units: number | null
  address: {
    address_line_1: string
    address_line_2: string
    address_town_or_city: string
    address_county: string
    address_postcode: string
    address_country: string
  }
}

/** Create an HOA community (property-manager consumer + community Host). */
export function useCreateHoaCommunity() {
  const qc = useQueryClient()
  return useMutation<HoaCommunityResponse, Error, HoaCommunityCreatePayload>({
    mutationFn: (data) =>
      api
        .post('/registrations/hoa-community/', data)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hoa-communities'] })
      qc.invalidateQueries({ queryKey: ['hoa-tree'] })
    },
  })
}

// ── Hierarchy (Developer → Community → Building → Unit) ──────────────

export interface HoaUnitNode {
  uuid: string
  type: 'unit'
  name: string
  /** The home's property address (line1, city, state, zip), if captured. */
  address?: string
  product?: string
  color?: string
  contractor?: string
  /** True once the unit has a real warranty registration (Holding). */
  registered?: boolean
  holding_uuid?: string
  /** Warranty registration number (e.g. "4E387D94"). Blank until registered. */
  registration_number?: string
  /** Warranty type/class - e.g. "Standard", "Preferred", "Extended Warranty". */
  warranty_type?: string
}
export interface HoaBuildingNode {
  uuid: string
  type: 'building'
  name: string
  default_warranty_option_uuid?: string
  default_warranty_name?: string
  units: HoaUnitNode[]
}
export interface HoaCommunityNode {
  uuid: string
  type: 'community'
  name: string
  address: string
  /** ISO timestamp the community was created - used to sort newest-first. */
  created_at?: string
  /** Its claims the viewer may see, counted on the server. */
  claim_count?: number
  buildings: HoaBuildingNode[]
}
export interface HoaDeveloperNode {
  uuid: string
  type: 'developer'
  name: string
  communities: HoaCommunityNode[]
}
/** Top-level nodes are developers or standalone communities. */
export type HoaTreeNode = HoaDeveloperNode | HoaCommunityNode

/** Fetch the full Developer → Community → Building → Unit tree. */
export function useHoaTree() {
  return useQuery<HoaTreeNode[]>({
    queryKey: ['hoa-tree'],
    queryFn: () =>
      api
        .get('/registrations/hoa/tree/')
        .then((r) => (r.data?.data ?? r.data)?.tree ?? []),
  })
}

function useHoaCreate<TPayload>(path: string) {
  const qc = useQueryClient()
  return useMutation<{ uuid: string; name: string }, Error, TPayload>({
    mutationFn: (data) =>
      api.post(path, data).then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hoa-tree'] })
    },
  })
}

/** Create a master developer. */
export function useCreateDeveloper() {
  return useHoaCreate<{ name: string; contact_email?: string }>(
    '/registrations/hoa/developers/',
  )
}

/** Create a building under a community. */
export function useCreateBuilding() {
  return useHoaCreate<{ community_uuid: string; name: string }>(
    '/registrations/hoa/buildings/',
  )
}

/** Create a unit under a building (with optional product/color/contractor). */
export function useCreateUnit() {
  return useHoaCreate<{
    building_uuid: string
    label: string
    product?: string
    color?: string
    contractor?: string
  }>('/registrations/hoa/units/')
}

export interface BulkUnitRow {
  label: string
  product?: string
  color?: string
  contractor?: string
}

export interface CorrespondenceAddress {
  line1?: string
  line2?: string
  city?: string
  state?: string
  zip_code?: string
  country?: string
}

/** Register a unit as a real warranty registration (owner + address + product). */
export function useRegisterUnit() {
  const qc = useQueryClient()
  return useMutation<
    { holding_uuid: string; product: string },
    Error,
    {
      unit_uuid: string
      homeowner_email: string
      homeowner_first_name?: string
      homeowner_last_name?: string
      product_uuid?: string
      product_name?: string
      correspondence_address?: CorrespondenceAddress
    }
  >({
    mutationFn: (data) =>
      api
        .post('/registrations/hoa/units/register/', data)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hoa-tree'] }),
  })
}

export interface BulkRegisterRow {
  label: string
  product?: string
  color?: string
  contractor?: string
  /** Warranty option name/label - resolved to a warranty on the backend. */
  warranty?: string
  owner_name?: string
  owner_first_name?: string
  owner_last_name?: string
  owner_email?: string
  owner_phone?: string
  /** Property/install address (the per-home location). */
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  /** Year the home was built (OC sheet "Year Built"). */
  year_built?: string
  /** Structure type, e.g. Single Family / Other - Duplex (OC "Property Type"). */
  property_type?: string
  /** Number of squares installed. */
  squares?: string
  /** Installer/contractor program / member number. */
  contractor_program_number?: string
  /** Install date (OC sheet "Date Installed"). */
  date_installed?: string
  /** Mailing address (separate from the property address). */
  mailing_line1?: string
  mailing_line2?: string
  mailing_city?: string
  mailing_state?: string
  mailing_zip?: string
  mailing_country?: string
  /** OC component flags (underlayment, starter_strip, ice_water, etc.). */
  components?: Record<string, boolean>
}

/** Set a building's default warranty (units inherit it on registration). */
export function useSetBuildingWarranty() {
  const qc = useQueryClient()
  return useMutation<
    { uuid: string; default_warranty_name: string },
    Error,
    { building_uuid: string; warranty_template_option_uuid: string }
  >({
    mutationFn: (data) =>
      api
        .post('/registrations/hoa/buildings/warranty/', data)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hoa-tree'] }),
  })
}

export interface BulkClaimRow {
  holding_uuid: string
  attribute_values?: Record<string, unknown>
}

export interface BulkFileClaimsResult {
  parent_uuid: string
  parent_claim_number: string
  created: number
  skipped: number
  errors: { row: number; error: string }[]
}

/**
 * Bulk-file unit claims for an HOA community under one umbrella (parent)
 * claim. Creates the HOA parent claim + one installed child claim per row.
 */
export function useBulkFileClaims() {
  const qc = useQueryClient()
  return useMutation<
    BulkFileClaimsResult,
    Error,
    {
      community_uuid: string
      parent_attribute_values?: Record<string, unknown>
      shared_attribute_values?: Record<string, unknown>
      rows: BulkClaimRow[]
    }
  >({
    mutationFn: (data) =>
      api
        .post('/claims/bulk-file-claims/', data)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hoa-tree'] })
      invalidateClaimsQueries(qc)
    },
  })
}

/** Bulk-create units AND register them (owners + addresses) from CSV rows. */
export function useBulkRegisterUnits() {
  const qc = useQueryClient()
  return useMutation<
    {
      created: number
      registered: number
      skipped: number
      errors: { row: number; error: string }[]
    },
    Error,
    { building_uuid: string; rows: BulkRegisterRow[] }
  >({
    mutationFn: (data) =>
      api
        .post('/registrations/hoa/units/bulk-register/', data)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hoa-tree'] }),
  })
}

/** Bulk-create units under a building. */
export function useBulkCreateUnits() {
  const qc = useQueryClient()
  return useMutation<
    { created: number; skipped: number; errors: { row: number; error: string }[] },
    Error,
    { building_uuid: string; rows: BulkUnitRow[] }
  >({
    mutationFn: (data) =>
      api
        .post('/registrations/hoa/units/bulk/', data)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hoa-tree'] }),
  })
}
